import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { nanoid } from 'nanoid'

import { NotificationManager } from '@/components/Notification'
import { AuthenticationService, cartService, punchoutService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectCartData } from '@/store/selectors/cartSelector'
import {
  selectHasSessionBasedShoppingCart,
  selectIsProcurementTechnicalUserCxml,
  selectUserOriginalLanguage,
} from '@/store/selectors/userSelector'
import { authenticationActions, selectUserId } from '@/store/slices/authenticationSlice'
import { emptyCart, getMiniCart } from '@/store/slices/cartSlice'
import { SESSION_BASED_EXPIRY_TIME } from '@/libs/constants'
import { isValidArray } from '@/libs/utils/array'
import {
  clearCookie,
  clearCookieForPunchoutSystem,
  getCookie,
  renewCookie,
  setCookie,
} from '@/libs/utils/cookies'
import { hasAllPropertiesValid } from '@/libs/utils/object'
import { convertCxmlDoc, submitOciForm, submitProcurementForm } from '@/libs/utils/punchoutHandler'

import { setSessionCookieKey } from './sessionCookieKeyStore'
import { PunchoutContext } from './usePunchoutContext'

const LOCAL_STORAGE_CART_SESSION_KEY = 'sessionId'
const DEFAULT_MANUFACTURER = 'Alfred K&#228;rcher SE &amp; Co. KG'

// let sessionCookieKey = "";

// export const getSessionCookieKey = () => sessionCookieKey;

const handleGetCachedData = async (sessionId: string) => {
  const response = await punchoutService.getCachedCxmlData(sessionId)
  return response?.data?.cxml
}

const handleCleanCachedData = async (sessionId: string) =>
  punchoutService.deleteCachedCxmlData(sessionId)

export const PunchoutProvider = ({ children }) => {
  const userId = useAppSelector(selectUserId)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isPreparingCXMLData, setIsPreparingCXMLData] = useState(false)

  const isProcurementUserCxml = useAppSelector(
    selectIsProcurementTechnicalUserCxml,
  )
  const cartData = useAppSelector(selectCartData)
  const hasSessionBasedShoppingCart = useAppSelector(
    selectHasSessionBasedShoppingCart,
  )
  const language = useAppSelector(selectUserOriginalLanguage)

  const COOKIE_KEY = useMemo(() => {
    return isProcurementUserCxml
      ? 'PUNCHOUT_SESSION_ID'
      : `${LOCAL_STORAGE_CART_SESSION_KEY}-${userId}`
  }, [isProcurementUserCxml, userId])

  const localSessionId = getCookie(COOKIE_KEY)
  const urlFromCookie = getCookie('HOOK_URL')

  const sessionId = useMemo(() => {
    if (localSessionId) {
      return localSessionId
    }

    const session = nanoid(36)

    const expiredTime = Date.now() + SESSION_BASED_EXPIRY_TIME
    setCookie({
      name: COOKIE_KEY,
      value: session,
      expires: expiredTime,
      ...(isProcurementUserCxml ? { sameSite: 'None', secure: true } : {}),
    })

    return session
  }, [COOKIE_KEY, localSessionId, isProcurementUserCxml])

  const clearShoppingCartSession = useCallback(async () => {
    if (isProcurementUserCxml) {
      const [emptyCartResult, cleanCachedDataResult] = await Promise.allSettled([
        cartService.emptyCart({ sessionId }),
        handleCleanCachedData(sessionId),
      ])

      if (emptyCartResult.status === 'rejected') {
        console.error('Failed to clear shopping cart session', emptyCartResult.reason)
      }

      if (cleanCachedDataResult.status === 'rejected') {
        console.error('Failed to clear cached cxml data', cleanCachedDataResult.reason)
      }
    }
    clearCookieForPunchoutSystem()
  }, [isProcurementUserCxml, sessionId])

  const renewShoppingCartSession = useCallback(() => {
    renewCookie(COOKIE_KEY, SESSION_BASED_EXPIRY_TIME)
  }, [COOKIE_KEY])

  const onSubmitFailedHandler = useCallback(() => {
    NotificationManager.error({
      message: 'notification.error.submitOrder',
      description: 'notification.error.noPunchoutUrlFound',
    })
  }, [])

  const onAfterSubmitCxmlData = useCallback(async () => {
    await Promise.all([
      clearShoppingCartSession(),
      AuthenticationService.signOut(),
    ])

    if (hasSessionBasedShoppingCart && isProcurementUserCxml) {
      clearCookieForPunchoutSystem()
    }
  }, [
    clearShoppingCartSession,
    hasSessionBasedShoppingCart,
    isProcurementUserCxml,
  ])

  const onSubmitCartViaOCI = useCallback(async () => {
    // must check user is tech and have valid session
    if (urlFromCookie) {
      submitOciForm({ cartData, url: urlFromCookie })

      dispatch(emptyCart())
      await clearShoppingCartSession()

      if (hasSessionBasedShoppingCart && !isProcurementUserCxml) {
        clearCookie(COOKIE_KEY)
      }

      dispatch(authenticationActions.signOut({ navigate }))
    } else {
      onSubmitFailedHandler()
    }
  }, [
    cartData,
    urlFromCookie,
    clearShoppingCartSession,
    dispatch,
    navigate,
    onSubmitFailedHandler,
    hasSessionBasedShoppingCart,
    isProcurementUserCxml,
    COOKIE_KEY,
  ])

  const prepareDataForCXMLTemplate = useCallback(
    (convertedCachedData) => {
      const { cXML } = convertedCachedData
      const { Header, Request } = cXML

      const payloadId = [nanoid(36), '@', globalThis.location.hostname].join('')
      const timestamp = new Date().toISOString()
      const templateType = getCookie('PUNCHOUT_TEMPLATE_TYPE')
      const isCoupaTemplate = templateType?.toLowerCase() === 'coupa'

      return {
        type: templateType,
        buyerCookie: Request?.PunchOutSetupRequest?.BuyerCookie,
        payloadId,
        timeStamp: timestamp,
        culture: isCoupaTemplate ? language : 'de-DE',
        supplier: {
          domain: Header?.To?.Credential?.domain,
          value: Header?.To?.Credential?.Identity,
        },
        sender: {
          domain: Header?.To?.Credential?.domain,
          value: Header?.To?.Credential?.Identity,
        },
        buyer: {
          domain: Header?.From?.Credential?.domain,
          value: Header?.From?.Credential?.Identity,
        },
        manufacturerName: DEFAULT_MANUFACTURER,
        unitOfMeasure: 'EA',
      }
    },
    [language],
  )

  const onSubmitCartToProcurement = useCallback(async () => {
    try {
      setIsPreparingCXMLData(true)
      const cachedData = await handleGetCachedData(sessionId)
      const convertedCachedData = convertCxmlDoc(cachedData)
      const cxmlData = prepareDataForCXMLTemplate(convertedCachedData)
      const { data: poom } = await punchoutService.generateDynamicCxmlTemplate({
        cxmlData,
        sessionId,
      })

      setIsPreparingCXMLData(false)
      if (hasAllPropertiesValid(convertedCachedData)) {
        await onAfterSubmitCxmlData()

        const formSubmitted = submitProcurementForm(convertedCachedData, poom)

        if (!formSubmitted) {
          console.error('Failed to submit procurement form - invalid URL')
        }
      } else {
        console.error('cached cxml data:', cxmlData)
        onSubmitFailedHandler()
        await onAfterSubmitCxmlData()
      }
    } catch (error) {
      setIsPreparingCXMLData(false)
      console.error('error while handling cxml cached data', error)
    }
  }, [
    sessionId,
    prepareDataForCXMLTemplate,
    onAfterSubmitCxmlData,
    onSubmitFailedHandler,
  ])

  const onReturnToProcurement = useCallback(async () => {
    try {
      const cachedData = await handleGetCachedData(sessionId)
      const convertedCachedData = convertCxmlDoc(cachedData)
      const cxmlData = prepareDataForCXMLTemplate(convertedCachedData)
      const { data: poom } = await punchoutService.generateDynamicCxmlTemplate({
        cxmlData: {
          ...cxmlData,
          jumpBackOnly: true,
        },
        sessionId,
      })

      if (hasAllPropertiesValid(convertedCachedData)) {
        await onAfterSubmitCxmlData()

        const formSubmitted = submitProcurementForm(convertedCachedData, poom)

        if (!formSubmitted) {
          console.error('Failed to submit procurement form - invalid URL')
        }
      } else {
        console.error('cached cxml data:', convertedCachedData?.cxml)
        onSubmitFailedHandler()
        await onAfterSubmitCxmlData()
      }
    } catch (error) {
      console.error('error while handling cxml cached data', error)
    }
  }, [
    sessionId,
    prepareDataForCXMLTemplate,
    onAfterSubmitCxmlData,
    onSubmitFailedHandler,
  ])

  const onSubmitPunchoutData = useCallback(async () => {
    if (cartData && isValidArray(cartData?.items)) {
      if (isProcurementUserCxml) {
        await onSubmitCartToProcurement()
      } else {
        await onSubmitCartViaOCI()
      }
    }
  }, [
    cartData,
    isProcurementUserCxml,
    onSubmitCartViaOCI,
    onSubmitCartToProcurement,
  ])

  useEffect(() => {
    if (hasSessionBasedShoppingCart && !COOKIE_KEY.includes('undefined')) {
      setSessionCookieKey(COOKIE_KEY)
      dispatch(getMiniCart())
    }
  }, [hasSessionBasedShoppingCart, COOKIE_KEY, dispatch])

  const contextValue = useMemo(
    () => ({
      sessionId,
      isPreparingCXMLData,
      clearShoppingCartSession,
      renewShoppingCartSession,
      onSubmitPunchoutData,
      onReturnToProcurement,
    }),
    [
      sessionId,
      isPreparingCXMLData,
      clearShoppingCartSession,
      renewShoppingCartSession,
      onSubmitPunchoutData,
      onReturnToProcurement,
    ],
  )

  return <PunchoutContext value={contextValue}>{children}</PunchoutContext>
}
